import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileAccordionSection } from '@/components/mobile/MobileAccordionSection';
import { 
  Camera, 
  Heart, 
  Palette, 
  Share2, 
  Sparkles, 
  Bell, 
  Trophy, 
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause
} from 'lucide-react';

const howToUseCopy: Record<string, { cta: string }> = {
  en: { cta: 'Create Your First Announcement' },
  es: { cta: 'Crea Tu Primer Anuncio' },
  fr: { cta: 'Créez Votre Première Annonce' },
  pt: { cta: 'Crie Seu Primeiro Anúncio' },
  ar: { cta: 'أنشئ إعلانك الأول' },
  ja: { cta: '最初のアナウンスを作成' },
  ko: { cta: '첫 번째 공지 만들기' },
  id: { cta: 'Buat Pengumuman Pertama Anda' },
};

export function HowToUseGuide() {
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const hCopy = howToUseCopy[language] || howToUseCopy.en;
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const guideSteps = [
    {
      id: 'create',
      title: t.howToUse.create.title,
      subtitle: t.howToUse.create.subtitle,
      icon: Heart,
      color: 'from-primary to-primary/70',
      steps: [
        { icon: Camera, label: t.howToUse.create.step1.label, desc: t.howToUse.create.step1.desc },
        { icon: Heart, label: t.howToUse.create.step2.label, desc: t.howToUse.create.step2.desc },
        { icon: Sparkles, label: t.howToUse.create.step3.label, desc: t.howToUse.create.step3.desc },
        { icon: Palette, label: t.howToUse.create.step4.label, desc: t.howToUse.create.step4.desc },
        { icon: Share2, label: t.howToUse.create.step5.label, desc: t.howToUse.create.step5.desc },
      ],
    },
    {
      id: 'dashboard',
      title: t.howToUse.dashboard.title,
      subtitle: t.howToUse.dashboard.subtitle,
      icon: LayoutDashboard,
      color: 'from-secondary to-secondary/70',
      features: [
        { icon: LayoutDashboard, label: t.howToUse.dashboard.viewAll.label, desc: t.howToUse.dashboard.viewAll.desc },
        { icon: Palette, label: t.howToUse.dashboard.editAnytime.label, desc: t.howToUse.dashboard.editAnytime.desc },
        { icon: Share2, label: t.howToUse.dashboard.copyLinks.label, desc: t.howToUse.dashboard.copyLinks.desc },
      ],
    },
    {
      id: 'ai-features',
      title: t.howToUse.aiFeatures.title,
      subtitle: t.howToUse.aiFeatures.subtitle,
      icon: Sparkles,
      color: 'from-accent to-accent/70',
      features: [
        { icon: Sparkles, label: t.howToUse.aiFeatures.messageWriting.label, desc: t.howToUse.aiFeatures.messageWriting.desc },
        { icon: Bell, label: t.howToUse.aiFeatures.reminders.label, desc: t.howToUse.aiFeatures.reminders.desc },
        { icon: Play, label: t.howToUse.aiFeatures.autoCompose.label, desc: t.howToUse.aiFeatures.autoCompose.desc },
      ],
    },
    {
      id: 'gamification',
      title: t.howToUse.gamification.title,
      subtitle: t.howToUse.gamification.subtitle,
      icon: Trophy,
      color: 'from-accent to-accent/70',
      levels: [
        { name: t.howToUse.gamification.levels.heartOpener, points: '0 pts', emoji: '💚' },
        { name: t.howToUse.gamification.levels.soulConnector, points: '50 pts', emoji: '💙' },
        { name: t.howToUse.gamification.levels.loveAmbassador, points: '100 pts', emoji: '💜' },
        { name: t.howToUse.gamification.levels.gratitudeChampion, points: '175 pts', emoji: '💛' },
        { name: t.howToUse.gamification.levels.legacyMaker, points: '250 pts', emoji: '❤️' },
      ],
      badges: [
        t.howToUse.gamification.badges.firstExpression,
        t.howToUse.gamification.badges.loveAmbassador,
        t.howToUse.gamification.badges.birthdayChampion,
        t.howToUse.gamification.badges.consistencyStar,
      ],
    },
  ];

  useEffect(() => {
    if (isPlaying && !isMobile) {
      intervalRef.current = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % guideSteps.length);
      }, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, guideSteps.length, isMobile]);

  const nextStep = () => {
    setCurrentStep((prev) => (prev + 1) % guideSteps.length);
    setIsPlaying(false);
  };

  const prevStep = () => {
    setCurrentStep((prev) => (prev - 1 + guideSteps.length) % guideSteps.length);
    setIsPlaying(false);
  };

  const step = guideSteps[currentStep];

  // Mobile: Accordion layout
  if (isMobile) {
    return (
      <section id="how-to-use" className="py-12 bg-gradient-to-b from-background to-muted/30">
        <div className="container px-4">
          <motion.div
            className="text-center mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl font-bold billboard-flash mb-2">
              {t.howToUse.title}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t.howToUse.subtitle}
            </p>
          </motion.div>

          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {guideSteps.map((guideStep, index) => (
              <MobileAccordionSection
                key={guideStep.id}
                title={guideStep.title}
                icon={<guideStep.icon className="h-5 w-5" />}
                defaultOpen={index === 0}
              >
                <div className="px-4 space-y-3">
                  <p className="text-sm text-muted-foreground">{guideStep.subtitle}</p>
                  
                  {/* Create steps */}
                  {guideStep.id === 'create' && guideStep.steps && (
                    <div className="space-y-2">
                      {guideStep.steps.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-sm">{item.label}</p>
                            <p className="text-xs text-muted-foreground">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Dashboard & AI features */}
                  {(guideStep.id === 'dashboard' || guideStep.id === 'ai-features') && guideStep.features && (
                    <div className="space-y-2">
                      {guideStep.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                          <feature.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-foreground text-sm">{feature.label}</p>
                            <p className="text-xs text-muted-foreground">{feature.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Gamification */}
                  {guideStep.id === 'gamification' && guideStep.levels && (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">{t.howToUse.gamification.levelsTitle}</p>
                        <div className="flex flex-wrap gap-2">
                          {guideStep.levels.map((level, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted text-xs">
                              <span>{level.emoji}</span>
                              <span className="font-medium">{level.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">{t.howToUse.gamification.badgesTitle}</p>
                        <div className="flex flex-wrap gap-2">
                          {guideStep.badges?.map((badge, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent/20 text-xs">
                              <Trophy className="h-3 w-3" />
                              <span>{badge}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </MobileAccordionSection>
            ))}
          </div>

          {/* CTA */}
          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link to="/signup">
              <Button size="lg" className="gap-2">
                <Heart className="h-5 w-5" />
                {hCopy.cta}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    );
  }

  // Desktop: Original carousel layout
  return (
    <section id="how-to-use" className="py-20 bg-gradient-to-b from-background to-muted/30">
      <div className="container">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold billboard-flash md:text-4xl mb-4">
            {t.howToUse.title}
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t.howToUse.subtitle}
          </p>
        </motion.div>

        {/* Navigation Dots */}
        <div className="flex justify-center gap-3 mb-8">
          {guideSteps.map((s, index) => (
            <button
              key={s.id}
              onClick={() => {
                setCurrentStep(index);
                setIsPlaying(false);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                currentStep === index
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-muted hover:bg-muted/80 text-muted-foreground'
              }`}
            >
              <s.icon className="h-4 w-4" />
              <span className="hidden sm:inline text-sm font-medium">{s.title.split(' ').slice(0, 2).join(' ')}</span>
            </button>
          ))}
        </div>

        {/* Main Content Card */}
        <div className="relative max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="overflow-hidden border-0 shadow-2xl">
                {/* Header */}
                <div className={`bg-gradient-to-r ${step.color} p-8 text-white`}>
                  <div className="flex items-center gap-4">
                    <motion.div
                      className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', delay: 0.2 }}
                    >
                      <step.icon className="h-10 w-10" />
                    </motion.div>
                    <div>
                      <h3 className="text-2xl font-bold">{step.title}</h3>
                      <p className="text-white/80">{step.subtitle}</p>
                    </div>
                  </div>
                </div>

                <CardContent className="p-8">
                  {/* Create Announcement Steps */}
                  {step.id === 'create' && step.steps && (
                    <div className="grid gap-4">
                      {step.steps.map((item, index) => (
                        <motion.div
                          key={item.label}
                          className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold">
                            {index + 1}
                          </div>
                          <item.icon className="h-6 w-6 text-primary" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground">{item.label}</h4>
                            <p className="text-sm text-muted-foreground">{item.desc}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Dashboard Features */}
                  {step.id === 'dashboard' && step.features && (
                    <div className="grid md:grid-cols-3 gap-6">
                      {step.features.map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          className="text-center p-6 rounded-xl bg-muted/50"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.15 }}
                        >
                          <div className="mx-auto w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
                            <feature.icon className="h-7 w-7 text-secondary" />
                          </div>
                          <h4 className="font-semibold text-foreground mb-2">{feature.label}</h4>
                          <p className="text-sm text-muted-foreground">{feature.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* AI Features */}
                  {step.id === 'ai-features' && step.features && (
                    <div className="grid md:grid-cols-3 gap-6">
                      {step.features.map((feature, index) => (
                        <motion.div
                          key={feature.label}
                          className="relative p-6 rounded-xl bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.15 }}
                        >
                          <motion.div
                            className="absolute -top-2 -right-2 w-6 h-6 bg-accent rounded-full flex items-center justify-center"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ repeat: Infinity, duration: 2, delay: index * 0.5 }}
                          >
                            <Sparkles className="h-3 w-3 text-accent-foreground" />
                          </motion.div>
                          <feature.icon className="h-8 w-8 text-accent mb-4" />
                          <h4 className="font-semibold text-foreground mb-2">{feature.label}</h4>
                          <p className="text-sm text-muted-foreground">{feature.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Gamification */}
                  {step.id === 'gamification' && step.levels && (
                    <div className="space-y-8">
                      {/* Levels */}
                      <div>
                        <h4 className="font-semibold text-foreground mb-4">{t.howToUse.gamification.levelsTitle}</h4>
                        <div className="flex flex-wrap gap-3 justify-center">
                          {step.levels.map((level, index) => (
                            <motion.div
                              key={level.name}
                              className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted"
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1, type: 'spring' }}
                            >
                              <span className="text-lg">{level.emoji}</span>
                              <span className="font-medium text-sm">{level.name}</span>
                              <span className="text-xs text-muted-foreground">({level.points})</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      {/* Badges */}
                      <div>
                        <h4 className="font-semibold text-foreground mb-4">{t.howToUse.gamification.badgesTitle}</h4>
                        <div className="flex flex-wrap gap-3 justify-center">
                          {step.badges?.map((badge, index) => (
                            <motion.div
                              key={badge}
                              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent/20 text-accent-foreground"
                              initial={{ opacity: 0, rotate: -10 }}
                              animate={{ opacity: 1, rotate: 0 }}
                              transition={{ delay: 0.5 + index * 0.1 }}
                            >
                              <Trophy className="h-4 w-4" />
                              <span className="text-sm font-medium">{badge}</span>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              size="icon"
              onClick={prevStep}
              className="rounded-full"
              aria-label="Previous step"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>

            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsPlaying(!isPlaying)}
                className="rounded-full"
                aria-label={isPlaying ? 'Pause autoplay' : 'Resume autoplay'}
              >
                {isPlaying ? (
                  <Pause className="h-5 w-5" />
                ) : (
                  <Play className="h-5 w-5" />
                )}
              </Button>
              <span className="text-sm text-muted-foreground">
                {currentStep + 1} / {guideSteps.length}
              </span>
            </div>

            <Button
              variant="outline"
              size="icon"
              onClick={nextStep}
              className="rounded-full"
              aria-label="Next step"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Link to="/signup">
            <Button size="lg" className="gap-2 text-lg px-8 py-6">
              <Heart className="h-5 w-5" />
              {hCopy.cta}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
